import { useEffect, useState } from 'react';
import { useLocation, useOutletContext, useParams } from 'react-router-dom';
import { connect, useDispatch } from 'react-redux';

import get from 'lodash/get';

import MasterComponent from '../../../common/MasterComponent';
import Popup from '../../../components/Popup';
import { partManagementActions } from '../../../store/reducers/part-management';
import AddPart from './addPart';
import { Typography, Grid, Stack, Box, Tooltip, useTheme } from '@mui/material';
import { GridValueGetterParams } from '@mui/x-data-grid';
import useNotification from '../../../common/notification';
import { addPartDefault, partsExcelSchema } from '../form';
import { pageActions } from '../../../store/reducers/page-actions';
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE } from '../../../utils/table-utils';
import { PartsDataRequestPayloadTypes } from '../../../store/models/parts-management';
import { ReactComponent as AlertIcon } from '../../../assets/icons/main/alertIcon.svg';
import {palette} from '../../../theme/palette';

interface PartManagementTypes {
  isPartManagementDataLoading: boolean;
  partManagementData: any;
  machineDetails: any;
  partManagementDataTotalRowCount: number;
  partManagementDataPageNumber: number;
  isAddPartSuccess: boolean;
  isAddPartError: boolean;
  isDeletePartSuccess: boolean;
  isDeletePartError: boolean;
  isUpdatePartSuccess: boolean;
  isUpdatePartError: boolean;
  partManagementMessage: string;
  partManagementError: boolean;
  isBulkUploadPartLoading: boolean;
  isBulkUploadPartSuccess: boolean;
  isBulkUploadPartError: boolean;
  partsStatusCode: number;
  searchItem: string;
  settingsData:any;
}

const PartManagement = ({
  isPartManagementDataLoading,
  partManagementData,
  machineDetails,
  partManagementDataTotalRowCount,
  partManagementDataPageNumber,
  isAddPartSuccess,
  isAddPartError,
  isDeletePartSuccess,
  isDeletePartError,
  isUpdatePartSuccess,
  isUpdatePartError,
  partManagementMessage,
  partManagementError,
  isBulkUploadPartLoading,
  isBulkUploadPartSuccess,
  isBulkUploadPartError,
  partsStatusCode,
  searchItem,
  settingsData
}: PartManagementTypes) => {
  const dispatch = useDispatch();
  const [msg, sendNotification] = useNotification();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [partTagsData, setPartTagsData] = useState<any>([]);
  const [isEdit, setIsEdit] = useState(false);
  const [partMasterData, setPartMasterData] = useState<any>([]);
  const { state, pathname } = useLocation();
  const [paramsValue, setParamValue] = useState<any>({});
  const [partData, setPartData] = useState<any>({ part_name: '', machine_id: 0 });
  const [confirmPopup, setConfirmPopup] = useState(false);
  const { machine_id } = useParams();
  const [sortingModel, setSortingModel] = useState({ field: '', sort: '' });
  var pluralize = require('pluralize');



  useEffect(() => {
    const data =
      partManagementData.map((item: any) => {
        return {
          ...item,
          id: item.part_id,
          part_tags:
            item.part_tags && item.part_tags.length > 0
              ? item.part_tags.map((val: any, index: any) => {
                return { ...val, id: index + 1 };
              })
              : [],
        };
      }) ?? [];
    setPartMasterData(data.filter((item: any) => item.machine_id === +(machine_id ?? '0')));
    setPartData({ part_name: '', machine_id: +(machine_id ?? '0') });

    //code for breadcrumbs value start
    if (pathname.length) {
      const pathnamesTemp: any = pathname.split('/').filter(Boolean);
      if (pathnamesTemp.length) {
        const pathnamesData: any = pathnamesTemp
          .filter((i: any, index: number) => index > 0)
          .map((item: any, index: number) => {
            if (index == 0) {
              return { route: pathnamesTemp[0] + '/' + pathnamesTemp[1], pathName: get(outletData,'page_name',item), navigate: true };
            } else if (index == 1) {
              return { route: '', pathName: machineDetails?.machine_name, navigate: false };
            } else {
              return { route: '', pathName: `${pluralize(get(outletData.things_parts, 'entity_name',item))}`, navigate: false };
            }
          });
        dispatch(pageActions.setPagePath(pathnamesData));
        return () => {
          const data: any = [];
          dispatch(pageActions.setPagePath(data));
        };
      }
    }
    //code for breadcrumbs value end
  }, [partManagementData, pathname]);

  useEffect(() => {
    if (isAddPartSuccess || isUpdatePartSuccess || isDeletePartSuccess || isBulkUploadPartSuccess) {
      dispatchPartsDataRequest({
        limit: DEFAULT_PAGE_SIZE,
        page:
          isDeletePartSuccess && partManagementData.length === 1 && partManagementDataPageNumber !== 1
            ? partManagementDataPageNumber - 1
            : partManagementDataPageNumber,
        machine_id,
      });
    }

    if (partsStatusCode != 409) {
      setIsModalOpen(false);
      setIsEdit(false);
      setPartData({ part_name: '', machine_id: +(machine_id ?? '0') });
      setPartTagsData([]);
    }

    // if (isAddPartError || partManagementError || isDeletePartError || isUpdatePartError || isBulkUploadPartError) {
    //   sendNotification({
    //     msg: partManagementMessage,
    //     variant: 'error',
    //     dispatchAction: partManagementActions.resetLoadingState(),
    //   });
    // }
  }, [
    isAddPartSuccess,
    isAddPartError,
    isDeletePartSuccess,
    isDeletePartError,
    isUpdatePartSuccess,
    isUpdatePartError,
    partManagementError,
    isBulkUploadPartSuccess,
    isBulkUploadPartError,
    partsStatusCode,
  ]);

  const handleEdit = (payload: any) => {
    setIsEdit(true);
    setPartData(payload);
    setPartTagsData(get(payload, 'part_tags', []).map((tag: any, i: number) => ({ ...tag, id: i + 1 })));
    setIsModalOpen(true);
  };

  const handleDelete = (payload: any) => {
    setPartData(payload);
    setConfirmPopup(true);
  };

  const handleActions = (data: any, type: any) => {
    if (type === 'confirm') {
      dispatch(partManagementActions.deletePartRequest(partData));
      setPartData({ part_name: '', machine_id: +(machine_id ?? '0') });
      setConfirmPopup(false);
    } else if (type === 'warning') {
      setPartData({ part_name: '', machine_id: +(machine_id ?? '0') });
      setConfirmPopup(false);
    }
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setIsEdit(false);
    setPartData({ part_name: '', machine_id: +(machine_id ?? '0') });
    setPartTagsData([]);
  };

  const closeAlertModal = () => {
    dispatch(partManagementActions.resetLoadingState());
  };

  const renderFormModal = () => (
    <AddPart
      isOpen={isModalOpen}
      partData={partData}
      setPartData={setPartData}
      partTagsData={partTagsData}
      setPartTagsData={setPartTagsData}
      isEdit={isEdit}
      closeModal={handleClose}
      partMasterData={partMasterData}
      pageData={get(outletData,'things_parts',{})}
    />
  );

  const handleBulkUpload = (data: any) => {
    const payloadData: any = { data: data, machine_id: machine_id };
    dispatch(partManagementActions.bulkUploadRequest(payloadData));
  };


  const outletData : any = get(useOutletContext(),'outletData',{});
  const themeData : any = useTheme();
  const dispatchPartsDataRequest = (payload: PartsDataRequestPayloadTypes) => {
    dispatch(
      partManagementActions.getPartManagementDataRequest({ ...sortingModel, search_item: searchItem, ...payload, pageName : get(outletData.things_parts,'entity_name','Part') })
    );
  };

  useEffect(() => {
    dispatchPartsDataRequest({
      limit: DEFAULT_PAGE_SIZE,
      page: DEFAULT_PAGE_NUMBER,
      machine_id,
    });
    setParamValue(state);
    setPartData({ part_name: '', machine_id: +(machine_id ?? '0') });
    return () => {
      dispatch(partManagementActions.resetLoadingState());
      dispatch(partManagementActions.resetPartManagementData());
    };
  }, [machine_id,settingsData]);

  const partLabel = (
    <Box sx={{
      display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '16px',
      '@media (max-width:1535px)': {
        marginBottom: '16px'
      },
      '@media (max-width:900px)': {
        marginBottom: '0px'
      }
    }} >

      <Typography variant='subtitle1' noWrap sx={{ maxWidth: '450px' }}>

        {get(outletData,'entity_name','Machine')} Name : <Tooltip title={machineDetails?.machine_name} placement='bottom' arrow><b>{machineDetails?.machine_name}</b></Tooltip>

      </Typography>

      <Typography variant='subtitle1' noWrap sx={{ maxWidth: '360px' }}>
        Manufacturer : <Tooltip title={machineDetails?.machine_manufacturer} placement='bottom' arrow><b>{machineDetails?.machine_manufacturer}</b></Tooltip>
      </Typography>

      <Typography variant='subtitle1' noWrap sx={{ maxWidth: '360px' }}>
        Type : <Tooltip title={machineDetails?.machine_type} placement='bottom' arrow><b>{machineDetails?.machine_type}</b></Tooltip>
      </Typography>

    </Box>
  );
  const columns: any = [
    {
      field: 'part_name',
      headerName: `${get(outletData.things_parts,'entity_name','Part') + ' Name'}`,
      flex: 1,
    },
  ];

  return (
    <>
      <MasterComponent
        heading={partLabel}
        isLoading={isPartManagementDataLoading}
        rows={partMasterData}
        columns={columns}
        isServerPagination
        totalRowCount={partManagementDataTotalRowCount}
        pageNumber={partManagementDataPageNumber}
        onPagination={(pageSize: number, pageNumber: number) => {
          dispatchPartsDataRequest({
            limit: pageSize,
            page: pageNumber,
            machine_id,
          });
        }}
        onSorting={(sortModel: { field: string; sort: string }) => {
          dispatchPartsDataRequest({
            limit: DEFAULT_PAGE_SIZE,
            page: partManagementDataPageNumber,
            machine_id,
            ...sortModel,
          });
          setSortingModel(sortModel);
        }}
        setIsModalOpen={setIsModalOpen}
        showImport={true}
        showExport={true}
        isTemplate={true}
        imporAction={handleBulkUpload}
        excelSchema={partsExcelSchema}
        exportData={addPartDefault}
        isFromPart={true}
        fileName={get(outletData.things_parts,'entity_name','part')+'_template'}
        entityName={get(outletData.things_parts,'entity_name','machine')}
        dataColumns={['part_name']}
        noRowsOverlay={() => (
          <Stack height='100%' alignItems='center' justifyContent='center'>
            {`${get(outletData.things_parts, 'entity_name', 'Part')}s data not available`}
          </Stack>
        )}
        actionsColumn={{
          edit: {
            onClick: (params: GridValueGetterParams) => handleEdit(params.row),
          },
          delete: {
            onClick: (params: GridValueGetterParams) => handleDelete(params.row),
          },
        }}
        showSearch
        searchPlaceholder={`${get(outletData.things_parts, 'entity_name', 'Part')} Name`}
        onSearch={(searchValue: string) => {
          dispatchPartsDataRequest({
            limit: DEFAULT_PAGE_SIZE,
            page: DEFAULT_PAGE_NUMBER,
            machine_id,
            search_item: searchValue,
          });
          dispatch(partManagementActions.onSearchForItem(searchValue));
        }}
      />
      <Popup
        open={confirmPopup}
        icon={<AlertIcon color={palette.warning.main}/>}
        message='Are you sure to delete this part?'
        cancel={true}
        confirmation={true}
        cancelLabel='No'
        confirmLabel='Yes'
        onConfirm={() => handleActions({}, 'confirm')}
        onClose={() => handleActions({}, 'warning')}
        onCancel={() => handleActions({}, 'warning')}
      />
      {isModalOpen && renderFormModal()}
      {/* {renderAlertModal()} */}
    </>
  );
};

const mapStateToProps = (state: any) => ({
  isPartManagementDataLoading: state.partManagement.isPartManagementDataLoading,
  partManagementData: state.partManagement.partManagementData,
  partManagementDataTotalRowCount: state.partManagement.partManagementDataTotalRowCount,
  partManagementDataPageNumber: state.partManagement.partManagementDataPageNumber,
  machineDetails: state.partManagement.machineDetails,
  isAddPartSuccess: state.partManagement.isAddPartSuccess,
  isAddPartError: state.partManagement.isAddPartError,
  isDeletePartSuccess: state.partManagement.isDeletePartSuccess,
  isDeletePartError: state.partManagement.isDeletePartError,
  isUpdatePartSuccess: state.partManagement.isUpdatePartSuccess,
  isUpdatePartError: state.partManagement.isUpdatePartError,
  partManagementMessage: state.partManagement.partManagementMessage,
  partManagementError: state.partManagement.partManagementError,
  isBulkUploadPartLoading: state.partManagement.isBulkUploadPartLoading,
  isBulkUploadPartSuccess: state.partManagement.isBulkUploadPartSuccess,
  isBulkUploadPartError: state.partManagement.isBulkUploadPartError,
  partsStatusCode: state.partManagement.partsStatusCode,
  searchItem: state.partManagement.searchItem,
  settingsData: state.settings.settingsData,
});

export default connect(mapStateToProps)(PartManagement);
